import { Box, Button, Card, Spinner, useBreakpoint } from '@palmetto/palmetto-components';
import { useNavigate } from 'react-router-dom';

import { UserPermissions } from 'types';

import useIsIntegratedPartnerUser from '../../../hooks/useIsIntegratedPartnerUser';
import { OrganizationNode } from '../../../types/Organizations';
import RequirePermissions from '../../auth/requirePermissions';

import OrganizationEquipmentSettings from './OrganizationEquipmentSettings';
import OrganizationListRow from './OrganizationListRow';
import OrganizationProperties from './OrganizationProperties';
import OrganizationUsers from './OrganizationUsers';

interface OrganizationSettingsProps {
  organization: OrganizationNode | undefined;
  isLoading?: boolean;
}

const OrganizationSettings = ({ organization, isLoading }: OrganizationSettingsProps) => {
  const navigate = useNavigate();
  const { isPhone } = useBreakpoint();
  const isIntegratedPartnerUser = useIsIntegratedPartnerUser();
  const isPalmettoRootOrg = ['sabal', 'light-epc'].includes(organization?.alias || '');

  return (
    <Box gap="lg">
      <OrganizationProperties organization={organization} isLoading={isLoading} />
      <OrganizationEquipmentSettings organization={organization} />
      <Card>
        <Box childGap="lg" padding="lg" alignItems="center" direction="row" justifyContent="space-between">
          <Box as="h3" fontWeight="medium" fontSize="md">
            Organizations
          </Box>
          {!isIntegratedPartnerUser && (
            <RequirePermissions permissions={[UserPermissions.admin]}>
              <Button
                as="a"
                navigate={() => navigate(`/settings/organizations/new?parent=${organization?.alias}`)}
                size="sm"
                variant="primary"
                tone="neutral"
                aria-label="create Organization"
                iconPrefix="add"
              >
                {!isPhone && 'Organization'}
              </Button>
            </RequirePermissions>
          )}
        </Box>
        {isLoading ? (
          <Box padding="0 lg lg lg">
            <Spinner />
          </Box>
        ) : isPalmettoRootOrg ? (
          <Box padding="0 lg lg lg">
            {`Due to the quantity, visible Organizations are intentionally hidden for ${organization?.name}, use search on
            the Organizations & Settings page to find a specific organization.`}
          </Box>
        ) : (
          <Box>
            {organization?.children?.map((item) => (
              <OrganizationListRow key={item.id} item={item} level={0} />
            ))}
          </Box>
        )}
      </Card>
      <OrganizationUsers />
    </Box>
  );
};

export default OrganizationSettings;
